import { type ReactNode, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Một đoạn màu trong thanh tiến độ của một hàng. */
export interface ProgressListSegment {
	key: string;
	label: string;
	value: number;
	/** Lớp `bg-*` của đoạn bar; cũng dùng cho chấm màu ở chú giải. */
	className: string;
}

/** Một ô số liệu hiện ra khi bung hàng. */
export interface ProgressListDetail {
	label: string;
	value: ReactNode;
	/** Lớp màu cho con số, ví dụ `text-green-700`. */
	className?: string;
}

export interface ProgressListRow {
	id: string;
	label: string;
	/** Dòng phụ dưới tên: mã ngành, mã tỉnh, ID người tiếp nhận... */
	sublabel?: ReactNode;
	total: number;
	/** Tử số của tỉ lệ in cạnh tên. Thường bằng đoạn bar đầu tiên. */
	primary: number;
	segments: ProgressListSegment[];
	details?: ProgressListDetail[];
}

interface ProgressListProps {
	title: string;
	description?: ReactNode;
	rows: ProgressListRow[];
	/** Nhãn của phần xám còn lại, ví dụ "Chưa tiếp nhận". */
	remainderLabel: string;
	/**
	 * Thứ tự đang áp dụng, và cách đổi nó.
	 *
	 * Component KHÔNG tự sắp `rows`: thứ tự do server quyết, vì endpoint có phân
	 * trang chỉ gửi về một trang và sắp lại tại đây sẽ cho ra "hạng nhất" chỉ
	 * đúng trong trang đó. Tab giữ state này và nạp lại dữ liệu khi nó đổi.
	 */
	sortBy: SortMode;
	onSortByChange: (sortBy: SortMode) => void;
	/** Làm mờ danh sách trong lúc nạp lại, giữ nguyên dữ liệu cũ. */
	isRefreshing?: boolean;
	/** Đánh số thứ tự trước tên. Tắt khi thứ tự không mang ý nghĩa. */
	showRank?: boolean;
	emptyText?: string;
	className?: string;
}

export type SortMode = "count" | "rate";

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
	{ value: "count", label: "Theo số lượng" },
	{ value: "rate", label: "Theo tỉ lệ hoàn thành" },
];

const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(1)}%`;

/**
 * Phần của `part` trong `total`, dùng cho cả tỉ lệ in cạnh tên lẫn bề rộng từng
 * đoạn bar.
 *
 * Một hàm cho cả ba chỗ dùng là có chủ ý: công thức này từng được viết lặp ba
 * lần, và ba bản sao thì sửa một chỗ là hai chỗ kia lệch mà không ai thấy —
 * con số bên cạnh tên sẽ không còn khớp với bề rộng của chính thanh bar.
 *
 * `total = 0` trả 0 chứ không trả `NaN`: một hạng mục không có sinh viên nào thì
 * tiến độ của nó là 0, còn `NaN` sẽ lọt vào `sort` và đẩy hàng đó lên đầu.
 */
export function ratioOf(part: number, total: number): number {
	return total > 0 ? part / total : 0;
}

/**
 * Sắp danh sách ngay tại client, CHỈ dùng khi client đã cầm trọn bộ dữ liệu.
 *
 * Ba tab ngành / phương thức xét tuyển / người tiếp nhận nhận cả mảng từ trang
 * cha và không phân trang, nên sắp ở đây cho ra đúng thứ tự mà server sẽ trả —
 * cùng một phép so trên cùng một tập hàng. Đổi lại, đổi thứ tự không phải gọi
 * lại bốn endpoint mà trang cha nạp chung trong một `Promise.all`.
 *
 * Endpoint CÓ phân trang thì không dùng được cách này: server cắt trang trước
 * khi client nhìn thấy dữ liệu, nên phải gửi `sortBy` lên. Xem `ProgressList`.
 */
export function sortRowsLocally(rows: ProgressListRow[], sortBy: SortMode): ProgressListRow[] {
	return [...rows].sort((a, b) => {
		if (sortBy === "rate") {
			const diff = ratioOf(b.primary, b.total) - ratioOf(a.primary, a.total);
			if (diff !== 0) return diff;
		}
		return b.total - a.total;
	});
}

/**
 * Danh sách tiến độ ngang, thay cho lưới card một-hạng-mục-một-card.
 *
 * Lưới card cũ cho mỗi hạng mục cả một khối số nhưng lại không cho so sánh giữa
 * chúng: muốn biết ngành nào chậm hơn phải tự nhẩm tỉ lệ qua từng card. Ở đây
 * mỗi hạng mục là một hàng, và các thanh dùng chung một thang nên chậm hay
 * nhanh nhìn ra ngay. Số liệu chi tiết không mất — nó nằm sau cú click vào hàng.
 *
 * Component không tự biết dữ liệu của tab nào: mỗi tab tự khai `segments` từ
 * đúng field nó có. Bốn tab chỉ có "đã tiếp nhận" nên chỉ khai một đoạn, phần
 * còn lại là xám; tab ngành có thêm nhóm đã đóng phí mà chưa tiếp nhận nên khai
 * hai đoạn.
 */
export function ProgressList({
	title,
	description,
	rows,
	remainderLabel,
	sortBy,
	onSortByChange,
	isRefreshing = false,
	showRank = true,
	emptyText = "Không có dữ liệu",
	className,
}: ProgressListProps) {
	/**
	 * Những hàng đang bung, không phải một hàng duy nhất.
	 *
	 * Người dùng thường so hai hạng mục với nhau — bung cái này mà cái kia tự thu
	 * lại thì phải nhớ bằng đầu, đúng thứ mà bảng số liệu sinh ra để khỏi phải làm.
	 */
	const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

	const toggleRow = (id: string) =>
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (!next.delete(id)) next.add(id);
			return next;
		});

	// Chú giải lấy từ hàng đầu: mọi hàng trong một tab dùng cùng bộ đoạn màu.
	const legendSegments = rows[0]?.segments ?? [];

	return (
		<Card className={className}>
			<CardContent className="p-4 md:p-6">
				<div className="mb-3 flex items-center justify-between gap-3">
					<div className="min-w-0">
						<h3 className="truncate text-base font-semibold md:text-lg">{title}</h3>
						{description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
					</div>
					<Select
						value={sortBy}
						onChange={(e) => onSortByChange(e.target.value as SortMode)}
						className="h-8 w-44 flex-shrink-0 px-2 text-xs"
						aria-label={`Sắp xếp ${title}`}
					>
						{SORT_OPTIONS.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</Select>
				</div>

				{rows.length === 0 ? (
					<p className="py-8 text-center text-sm text-muted-foreground">{emptyText}</p>
				) : (
					// Danh sách cuộn theo cả trang, không có vùng cuộn riêng.
					//
					// Một vùng cuộn lồng trong trang bắt người xem phải nhận ra con lăn
					// đang tác động lên khung nào, và ở cuối danh sách thì cuộn tiếp
					// không đi đâu cả cho tới khi trỏ chuột rời khỏi khung. Số hạng mục
					// ở đây nhỏ — 57 tỉnh, 39 ngành, 8 phương thức, và trường THPT thì
					// đã phân trang 24 dòng — nên không có gì để cắt ngắn.
					//
					// Chú giải vì thế cũng thôi dính: không còn khung cuộn để dính vào,
					// và một chú giải dính vào đỉnh cửa sổ sẽ tranh chỗ với các card khác
					// trên cùng trang.
					//
					// Nạp lại thì làm mờ tại chỗ, không tháo bảng ra rồi dựng lại.
					<div
						className={cn("transition-opacity", isRefreshing && "pointer-events-none opacity-60")}
					>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b bg-card pb-2 text-xs text-muted-foreground">
							{legendSegments.map((segment) => (
								<span key={segment.key} className="flex items-center gap-1.5">
									<span className={cn("h-2 w-2 rounded-full", segment.className)} />
									{segment.label}
								</span>
							))}
							<span className="flex items-center gap-1.5">
								<span className="h-2 w-2 rounded-full bg-secondary" />
								{remainderLabel}
							</span>
						</div>

						{/* Hai cột, đọc theo chữ Z: 1-2 rồi 3-4. Đó là thứ tự mặc định của
						    CSS grid nên không cần đảo gì — xếp theo cột (1-3 / 2-4) mới là
						    thứ phải tự dựng, và nó bắt mắt người đọc nhảy dọc rồi quay lại.

						    Một cột trên màn hình hẹp: hai cột ở đó chỉ còn vài chục pixel
						    cho tên, và tên trường THPT thì dài.

						    Viền đặt trên từng ô thay vì `divide-y` của khung ngoài: trong
						    lưới, `divide-y` chỉ vẽ giữa các phần tử liền kề theo DOM nên nó
						    kẻ nhầm cả vào giữa hai cột. */}
						<div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
							{rows.map((row, index) => (
								<ProgressListItem
									key={row.id}
									row={row}
									rank={showRank ? index + 1 : undefined}
									isExpanded={expandedIds.has(row.id)}
									onToggle={() => toggleRow(row.id)}
								/>
							))}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

interface ProgressListItemProps {
	row: ProgressListRow;
	rank?: number;
	isExpanded: boolean;
	onToggle: () => void;
}

function ProgressListItem({ row, rank, isExpanded, onToggle }: ProgressListItemProps) {
	const hasDetails = (row.details?.length ?? 0) > 0;
	const ratio = ratioOf(row.primary, row.total);

	return (
		// `self-start`: grid mặc định kéo mọi ô trong một hàng cao bằng ô cao nhất
		// (`align-items: stretch`). Khi ô bên cạnh bung chi tiết ra, ô này bị kéo
		// theo và biến thành một hộp cao gần như rỗng, đường viền nằm tít dưới đáy —
		// nội dung không đổi mà hiển thị thì đổi. `self-start` cho ô cao đúng bằng
		// nội dung của chính nó, nên việc bung của hàng xóm không chạm tới nó.
		<div className="self-start border-b">
			{/* Nút thật, không `div` gắn onClick: hàng phải bấm được bằng bàn phím và
			    đọc được trạng thái bung/thu bằng trình đọc màn hình. */}
			<button
				type="button"
				onClick={hasDetails ? onToggle : undefined}
				aria-expanded={hasDetails ? isExpanded : undefined}
				disabled={!hasDetails}
				className={cn(
					"w-full rounded px-1 py-2.5 text-left transition-colors",
					hasDetails && "hover:bg-accent",
				)}
			>
				{/* Chiều cao tối thiểu đúng hai dòng chữ, để thanh bar bên dưới luôn
				    nằm cùng một độ cao ở cả hai cột — tên một dòng và tên hai dòng đều
				    chiếm cùng chỗ.

				    Đây KHÔNG dùng `h-full` + `mt-auto` như bản trước: cách đó đẩy bar
				    xuống đáy Ô, mà ô lại bị grid kéo cao bằng ô cạnh nó, nên khi một ô
				    bung chi tiết ra thì bar của ô kia rơi xuống tận đáy. Cố định chiều
				    cao vùng tên thì việc bung của ô này không chạm tới ô kia.

				    Tên dài quá hai dòng vẫn đẩy bar xuống, nhưng đó là trường hợp hiếm
				    và vẫn đọc được, hơn là cắt mất chữ. */}
				<div className="flex min-h-10 items-start gap-2">
					{hasDetails ? (
						isExpanded ? (
							<ChevronDown className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
						) : (
							<ChevronRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
						)
					) : (
						<span className="w-3.5 flex-shrink-0" />
					)}
					{/* Cho xuống dòng thay vì cắt: tên trường THPT và tên ngành dài hơn
					    hẳn một nửa bề ngang card, và cắt đi thì hai trường khác nhau có
					    thể hiện ra giống hệt nhau. `break-words` để một từ dài bất
					    thường cũng không đẩy tràn cột. */}
					<span className="min-w-0 flex-1 break-words text-sm font-medium">
						{rank !== undefined && <span className="text-muted-foreground">{rank}. </span>}
						{row.label}
					</span>
					{/* Bề rộng cố định và căn phải cho hai ô số, để chúng thành hai CỘT
					    thẳng hàng dọc qua mọi hàng.
					    Không có bề rộng thì `flex` đẩy chúng tới một vị trí khác nhau ở
					    mỗi hàng, tuỳ độ dài tên và độ rộng chính con số — mắt không có
					    mốc nào để dò dọc, và đó mới là thứ gây mỏi khi lướt 57 tỉnh, chứ
					    không phải khoảng cách giữa các hàng. */}
					<span className="w-14 flex-shrink-0 text-right text-sm font-semibold text-green-700 dark:text-green-400">
						{formatPercent(ratio)}
					</span>
					<span className="w-24 flex-shrink-0 text-right text-xs text-muted-foreground">
						({row.primary.toLocaleString()}/{row.total.toLocaleString()})
					</span>
				</div>

				{/* Đường kẻ trắng giữa các đoạn, không phải để trang trí.
				    Hai đoạn cùng một sắc chỉ khác độ sáng, và độ tương phản giữa chúng
				    (`green-600` với `green-300`) là 2,35:1, dưới ngưỡng 3:1 của WCAG cho
				    đối tượng đồ hoạ. Không thể nâng lên bằng cách đổi màu: `green-600`
				    có độ sáng 0,269, nên một màu cách nó 3:1 phải hoặc tối hơn hẳn,
				    hoặc gần trắng tới mức biến mất trên nền card.
				    Đường kẻ giải quyết bằng cấu trúc: ranh giới nhìn thấy được mà không
				    cần dựa vào màu, kể cả với người mù màu hay khi in đen trắng. Con số
				    `(x/y)` cạnh mỗi hàng là kênh dư thừa thứ hai. */}
				<div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-secondary">
					{row.segments.map((segment, index) => (
						<div
							key={segment.key}
							className={cn(
								"h-full transition-all duration-300",
								index > 0 && "border-l border-white",
								segment.className,
							)}
							style={{ width: `${ratioOf(segment.value, row.total) * 100}%` }}
							title={`${segment.label}: ${segment.value.toLocaleString()}`}
						/>
					))}
				</div>
			</button>

			{/* Chỉ tới 3 cột: khối này nằm trong MỘT ô của lưới hai cột, tức chỉ có
			    nửa bề ngang card. Bốn cột ở đó là nhồi nhét, nhãn bị gãy dòng giữa
			    chừng. */}
			{isExpanded && hasDetails && (
				<div className="mb-2 ml-5 grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-muted p-3 lg:grid-cols-3">
					{row.sublabel && (
						<div className="col-span-full text-xs text-muted-foreground">{row.sublabel}</div>
					)}
					{row.details?.map((detail) => (
						<div key={detail.label}>
							<p className="text-xs text-muted-foreground">{detail.label}</p>
							<p className={cn("text-sm font-semibold", detail.className)}>
								{typeof detail.value === "number" ? detail.value.toLocaleString() : detail.value}
							</p>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
